<template>
    <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content" :style="{ maxWidth }">
            <button class="close-button" @click="closeModal">&times;</button>
            <div class="modal-header">
                <slot name="header">
                    <h5 class="modal-title">{{ computedTitle }}</h5>
                </slot>
            </div>
            <form @submit.prevent="submitForm">
                <div class="modal-body" :style="{ maxHeight: bodyMaxHeight }">
                    <slot></slot>
                </div>
                <div class="modal-footer">
                    <slot name="footer">
                        <argon-button v-if="showCancel" color="light" variant="gradient" @click="closeModal">
                            {{ cancelText || 'Cancelar' }}
                        </argon-button>
                        <argon-button
                            v-if="showConfirm"
                            color="primary"
                            variant="gradient"
                            type="submit"
                        >
                            {{ confirmText || (isEditMode ? 'Actualizar' : 'Crear') }}
                        </argon-button>
                    </slot>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue';
import ArgonButton from "@/components/ArgonButton.vue";

const props = defineProps({
    modelValue: Boolean,
    isEditMode: {
        type: Boolean
    },
    title: {
        type: String,
        default: ''
    },
    confirmText: {
        type: String,
        default: ''
    },
    cancelText: {
        type: String,
        default: ''
    },
    showConfirm: {
        type: Boolean,
        default: true
    },
    showCancel: {
        type: Boolean,
        default: true
    },
    maxWidth: {
        type: String,
        default: '500px'
    },
    bodyMaxHeight: {
        type: String,
        default: '70vh'
    }
});

const emit = defineEmits(['update:modelValue', 'submit-entity']);

const computedTitle = computed(() => {
    if (props.title) return props.title;
    return props.isEditMode ? 'Actualizar' : 'Crear';
});

const submitForm = () => {
    emit('submit-entity');
};

const closeModal = () => {
    emit('update:modelValue', false);
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: #fff;
    border-radius: 8px;
    position: relative;
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
}

.modal-header {
    margin-bottom: 1rem;
}

.modal-body {
    overflow-y: auto;
    flex: 1;
    padding-right: 8px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem 1rem;
    border-top: 1px solid #e9ecef;
}

/* Ajuste de tamaño para botones Argon o nativos dentro del footer */
.modal-footer :deep(button),
.modal-footer :deep(.btn) {
    padding: 0.5rem 1.1rem;
    font-size: 0.95rem;
    border-radius: 10px;
    font-weight: 600;
    transition: all 0.2s ease;
}

/* Botones nativos (no .btn) con estilo suave */
.modal-footer :deep(button:not(.btn)) {
    background: linear-gradient(135deg, #f8fbff, #eef2f7);
    color: #1f2937;
    border: 1px solid #d8dee9;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12);
}

.modal-footer :deep(button:not(.btn):hover) {
    background: linear-gradient(135deg, #e8f0ff, #e4e9f2);
    border-color: #c7d2e2;
    transform: translateY(-1px);
}

/* Variante secundaria (Cerrar) */
.modal-footer :deep(.btn-secondary),
.modal-footer :deep(button.btn-secondary) {
    background: linear-gradient(135deg, #f8fbff, #e9edf5);
    color: #334155;
    border: 1px solid #d7deea;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.1);
}

.modal-footer :deep(.btn-secondary:hover),
.modal-footer :deep(button.btn-secondary:hover) {
    background: linear-gradient(135deg, #e8efff, #e2e8f0);
    border-color: #c7d2e2;
    transform: translateY(-1px);
}
</style>
